import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Swords,
  Target,
  Activity,
  Store,
  Zap,
  ShieldAlert,
} from "lucide-react";
import { callBackend } from "../utils/api";
import { useNotify } from "./Notification";

// Helper for consistent alliance colors
const getAllianceColor = (tag) => {
  if (!tag) return "#3f3f46";
  let hash = 0;
  for (let i = 0; i < tag.length; i++) {
    hash = tag.charCodeAt(i) + ((hash << 5) - hash);
  }
  return `hsl(${Math.abs(hash) % 360}, 70%, 50%)`;
};

export default function CityControl({ userStats, onActionSuccess }) {
  const [districts, setDistricts] = useState([]);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [availableCrimes, setAvailableCrimes] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [activeTab, setActiveTab] = useState("intel"); // intel, crimes, business
  const [loading, setLoading] = useState(true);
  const { addNotification } = useNotify();
  const [crimeLogs, setCrimeLogs] = useState([]);

  // Calculate how many districts the user's alliance currently controls
  const controlledCount = useMemo(() => {
    if (!userStats.alliance_id) return 0;
    return districts.filter(
      (d) => d.controlling_alliance_id === userStats.alliance_id,
    ).length;
  }, [districts, userStats.alliance_id]);

  const isAtControlLimit = controlledCount >= 3;

  // Unified Fetching for Districts and Business Types
  const fetchInitialData = useCallback(async () => {
    try {
      const [distData, bizData] = await Promise.all([
        callBackend("/districts/status"),
        callBackend("/businesses/types"),
      ]);
      setDistricts(distData);
      setBusinessTypes(bizData);

      setSelectedDistrict((currentDistrict) => {
        if (currentDistrict) {
          return (
            distData.find((district) => district.id === currentDistrict.id) ||
            currentDistrict
          );
        }
        if (distData.length === 0) return null;

        const accessibleDistrict = distData.find(
          (district) =>
            district.min_active_crime_level !== null &&
            Number(district.min_active_crime_level) <= Number(userStats.level),
        );
        return accessibleDistrict || distData[0];
      });
    } catch (err) {
      addNotification("Intelligence gathering failed.", "error");
    } finally {
      setLoading(false);
    }
  }, [addNotification, userStats.level]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Fetch regional crimes when a district is selected
  useEffect(() => {
    if (selectedDistrict) {
      if (activeTab === "committed crimes") {
        callBackend(`/districts/${selectedDistrict.id}/committedcrimes`)
          .then((data) => setCrimeLogs(data))
          .catch(() => addNotification("Tactical feed jammed.", "error"));
      } else if (activeTab === "crimes") {
        callBackend(`/crimes/list?districtId=${selectedDistrict.id}`)
          .then((data) => setAvailableCrimes(data))
          .catch(() => addNotification("Underworld signal lost.", "error"));
      }
    }
  }, [selectedDistrict, activeTab, addNotification]);

  const handleChallenge = async (districtId) => {
    try {
      const data = await callBackend(`/districts/challenge/${districtId}`, {
        method: "POST",
        body: { clientStats: userStats },
      });
      addNotification(
        data.message,
        data.winner === "attacker" ? "success" : "error",
      );
      fetchInitialData();
      onActionSuccess();
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handleCommitCrime = async (crime) => {
    if (userStats.level < crime.min_level) {
      addNotification(
        `You need Level ${crime.min_level} for ${crime.name}.`,
        "error",
      );
      return;
    }

    if (userStats.turns < crime.turn_cost) {
      addNotification(
        `You need ${crime.turn_cost} turns for ${crime.name}.`,
        "error",
      );
      return;
    }

    try {
      const data = await callBackend(`/crimes/commit/${crime.id}`, {
        method: "POST",
        body: {
          clientStats: userStats,
          districtId: selectedDistrict.id,
        },
      });
      addNotification(data.message, data.success ? "success" : "error");
      onActionSuccess();
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handleBuyBusiness = async (bizId) => {
    try {
      const data = await callBackend("/businesses/buy", {
        method: "POST",
        body: {
          districtId: selectedDistrict.id,
          businessId: bizId,
          clientStats: userStats,
        },
      });
      addNotification(data.message, "success");
      onActionSuccess();
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handleRetaliate = async (targetId) => {
    try {
      const data = await callBackend(`/attack/${targetId}`, {
        method: "POST",
        body: { clientStats: userStats },
      });
      addNotification(
        data.message,
        data.winner === "attacker" ? "success" : "error",
      );
      onActionSuccess();
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const CITY_CONTROL_TABS = ["intel", "crimes", "business", "committed crimes"];

  if (loading)
    return (
      <div className="flex items-center justify-center h-96 font-mono text-blue-500 animate-pulse">
        [ INITIALIZING SATELLITE UPLINK... ]
      </div>
    );

  return (
    <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 font-mono bg-black p-4 border-2 border-zinc-900">
      {/* Tactical Map Area */}
      <div className="xl:col-span-3 relative bg-zinc-950 border-2 border-zinc-800 overflow-hidden min-h-[600px]">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40 mix-blend-luminosity grayscale hover:grayscale-0 transition-all duration-700"
          style={{
            backgroundImage: `url('/assets/textures/TerritoryMap.png')`,
          }}
        />

        {districts.map((d) => {
          const color = getAllianceColor(d.alliance_tag);
          const isSelected = selectedDistrict?.id === d.id;
          return (
            <button
              key={d.id}
              onClick={() => setSelectedDistrict(d)}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-300 z-10"
              style={{
                left: `${d.posX}%`,
                top: `${d.posY}%`,
                width: `${d.size}%`,
                height: `${d.size}%`,
              }}
            >
              <div
                className={`w-full h-full border-2 rotate-45 flex items-center justify-center transition-all ${
                  isSelected
                    ? "scale-110 shadow-[0_0_20px_white] border-white z-20"
                    : "border-opacity-50"
                }`}
                style={{
                  backgroundColor: isSelected ? `${color}66` : `${color}22`,
                  borderColor: isSelected ? "#fff" : color,
                }}
              >
                <div className="-rotate-45 text-[10px] font-black text-white drop-shadow-lg uppercase text-center p-1">
                  {d.alliance_tag ? `[${d.alliance_tag}]` : "---"}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Integrated Detail Panel */}
      <div className="xl:col-span-1 flex flex-col space-y-4">
        {selectedDistrict ? (
          <div className="bg-zinc-900 border-2 border-zinc-800 flex-1 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-zinc-800 bg-black/40">
              <h3 className="text-xl font-black text-white italic uppercase tracking-tighter">
                {selectedDistrict.name}
              </h3>
              <div className="flex items-center gap-2 text-[10px] text-zinc-500 font-bold uppercase mt-1">
                <Activity size={10} className="text-green-500" /> Live Data
                Stream
              </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-zinc-800 bg-black text-[10px] font-black uppercase">
              {CITY_CONTROL_TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 py-3 transition-all ${
                    activeTab === tab
                      ? "bg-zinc-800 text-white"
                      : "text-zinc-600 hover:text-zinc-400"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="p-4 overflow-y-auto flex-1 space-y-4">
              {activeTab === "intel" && (
                <div className="space-y-4">
                  <div className="bg-black/40 p-3 border border-zinc-800 space-y-2">
                    <div className="flex justify-between text-[10px] uppercase font-bold">
                      <span className="text-zinc-500">Controller</span>
                      <span className="text-white italic">
                        {selectedDistrict.alliance_name || "Street Gangs"}
                      </span>
                    </div>
                    <div className="flex justify-between text-[10px] uppercase font-bold">
                      <span className="text-zinc-500">Passive Payout</span>
                      <span className="text-green-500">
                        ${selectedDistrict.base_bonus_cash.toLocaleString()}
                        /tick
                      </span>
                    </div>

                    {/* Territory Control Status UI */}
                    {userStats.alliance_id && (
                      <div className="flex justify-between text-[10px] uppercase font-bold border-t border-zinc-800 pt-2 mt-2">
                        <span className="text-zinc-500">Alliance Control</span>
                        <span
                          className={
                            isAtControlLimit
                              ? "text-red-500 animate-pulse"
                              : "text-blue-400"
                          }
                        >
                          {controlledCount} / 3 Districts
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Overstretch Warning Box */}
                  {isAtControlLimit &&
                    selectedDistrict.controlling_alliance_id !==
                      userStats.alliance_id && (
                      <div className="bg-red-900/20 border border-red-900 p-2 flex items-start gap-2">
                        <ShieldAlert
                          size={14}
                          className="text-red-500 shrink-0 mt-0.5"
                        />
                        <span className="text-[9px] text-red-400 font-bold uppercase leading-tight">
                          Warning: Strategic Overstretch. Alliance at 3/3
                          capacity.
                        </span>
                      </div>
                    )}

                  <button
                    onClick={() => handleChallenge(selectedDistrict.id)}
                    disabled={
                      userStats.level <
                        selectedDistrict.min_level_to_challenge ||
                      userStats.turns < 10 ||
                      (isAtControlLimit &&
                        selectedDistrict.controlling_alliance_id !==
                          userStats.alliance_id) ||
                      selectedDistrict.controlling_alliance_id ===
                        userStats.alliance_id
                    }
                    className="w-full bg-red-700 hover:bg-white hover:text-black text-black font-black py-4 uppercase italic text-xs flex items-center justify-center gap-2 transition-all skew-x-[-12deg] disabled:bg-zinc-800 disabled:text-zinc-600"
                  >
                    <Target size={16} />
                    {selectedDistrict.controlling_alliance_id ===
                    userStats.alliance_id
                      ? "Sector Secured"
                      : "Initiate Takeover (10 Turns)"}
                  </button>
                  <p className="text-[9px] text-zinc-600 text-center uppercase italic">
                    {selectedDistrict.description}
                  </p>
                </div>
              )}

              {activeTab === "crimes" && (
                <div className="space-y-3">
                  {availableCrimes.length > 0 ? (
                    availableCrimes.map((c) => {
                      const levelLocked = userStats.level < c.min_level;
                      const turnsLocked = userStats.turns < c.turn_cost;
                      const locked = levelLocked || turnsLocked;
                      const buttonLabel = levelLocked
                        ? `Requires Level ${c.min_level}`
                        : turnsLocked
                          ? `Requires ${c.turn_cost} Turns`
                          : `Do The Job (${c.turn_cost} Turns)`;

                      return (
                        <div
                          key={c.id}
                          className="border border-zinc-800 p-3 bg-black/40 hover:border-red-900 transition-colors group"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex flex-col">
                              <span className="text-xs font-black text-white uppercase italic">
                                {c.name}
                              </span>
                              {c.crime_type !== "universal" && (
                                <span className="text-[8px] text-yellow-500 font-bold uppercase">
                                  ✦ {c.crime_type} Contract
                                </span>
                              )}
                            </div>
                            <span className="text-[10px] text-red-600 font-bold">
                              {Math.round((1 - c.success_chance) * 100)}% RISK
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleCommitCrime(c)}
                            aria-disabled={locked}
                            className={`w-full py-2 text-[9px] font-black uppercase transition-all ${
                              locked
                                ? "cursor-not-allowed bg-zinc-900 text-zinc-600 hover:text-red-400"
                                : "bg-zinc-800 text-white group-hover:bg-red-700"
                            }`}
                          >
                            <Zap size={12} className="inline mr-1" />{" "}
                            {buttonLabel}
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-10 text-[10px] text-zinc-700 font-black uppercase italic">
                      No active contracts in this sector
                    </div>
                  )}
                </div>
              )}

              {activeTab === "business" && (
                <div className="space-y-3">
                  {businessTypes.map((biz) => {
                    const canAfford = userStats.cash >= biz.base_price;
                    return (
                      <div
                        key={biz.id}
                        className="border border-zinc-800 p-3 bg-black/40 hover:border-blue-900 transition-colors"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-xs font-black text-white uppercase italic">
                            {biz.name}
                          </span>
                          <span
                            className={`text-[10px] font-black ${canAfford ? "text-green-500" : "text-red-600"}`}
                          >
                            ${biz.base_price.toLocaleString()}
                          </span>
                        </div>
                        <button
                          onClick={() => handleBuyBusiness(biz.id)}
                          disabled={!canAfford}
                          className="w-full py-2 bg-zinc-800 hover:bg-blue-600 text-white text-[9px] font-black uppercase transition-all"
                        >
                          <Store size={12} className="inline mr-1" /> Establish
                          Front
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {activeTab === "committed crimes" && (
                <div className="space-y-2 overflow-y-auto max-h-[400px] pr-2 custom-scrollbar">
                  {crimeLogs.map((log) => (
                    <div
                      key={log.id}
                      className="border-l-2 border-zinc-800 bg-black/40 p-2 text-[10px] font-mono hover:bg-zinc-900/40 transition-all"
                    >
                      <div className="flex justify-between text-zinc-600 mb-1 uppercase font-bold">
                        <span>
                          {new Date(log.created_at).toLocaleTimeString()}
                        </span>
                        <span
                          className={
                            log.is_success ? "text-green-500" : "text-red-500"
                          }
                        >
                          {log.is_success ? "SUCCESS" : "BUSTED"}
                        </span>
                      </div>
                      <div className="text-white mb-2 leading-tight">
                        <span className="text-blue-400 font-black">
                          {log.alliance_tag ? `[${log.alliance_tag}] ` : ""}
                          {log.username}
                        </span>
                        <span className="text-zinc-500 mx-1">hit</span>
                        <span className="text-red-500 font-bold uppercase italic">
                          {log.crime_name}
                        </span>
                      </div>

                      {/* Retaliation Button */}
                      <button
                        onClick={() => handleRetaliate(log.user_id)}
                        className="w-full bg-zinc-800 hover:bg-red-700 text-white font-black py-1 uppercase text-[9px] transition-all flex items-center justify-center gap-1"
                      >
                        <Swords size={10} /> Retaliate (2 Turns)
                      </button>
                    </div>
                  ))}
                  {crimeLogs.length === 0 && (
                    <div className="text-center py-10 text-[10px] text-zinc-700 uppercase italic">
                      Sector is cold. No reported activity.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="h-full border-2 border-dashed border-zinc-800 flex items-center justify-center p-10 text-center text-[10px] text-zinc-700 uppercase font-black italic">
            INITIALIZE TACTICAL DATA UPLINK
          </div>
        )}
      </div>
    </div>
  );
}
